// Near-black ink rather than pure #000 — softer against the mid-tone factiii
// palette while still clearing WCAG AA on every color in it.
export const READABLE_INK = '#111827';
export const READABLE_PAPER = '#FFFFFF';

const toLinear = (channel: number): number => {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a `#rrggbb` (or `#rgb`) color. */
const relativeLuminance = (hex: string): number => {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const int = parseInt(full, 16);
  return (
    0.2126 * toLinear((int >> 16) & 255) +
    0.7152 * toLinear((int >> 8) & 255) +
    0.0722 * toLinear(int & 255)
  );
};

/** WCAG contrast ratio between two `#rrggbb` colors — 1 (same) to 21 (b/w). */
export const getContrastRatio = (a: string, b: string): number => {
  const [x, y] = [relativeLuminance(a), relativeLuminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};

/**
 * Pick the more readable of white or near-black ink for text on `background`.
 *
 * White is NOT a safe default on the factiii palette: against the amber, lime,
 * green, teal and cyan entries it lands near 2:1, well under the 3:1 WCAG AA
 * floor for large text, while ink clears 7:1 on all five. Only indigo and
 * violet actually read better in white. Measuring per color instead of picking
 * one is what keeps every factiii monogram legible.
 */
export const getReadableTextColor = (background: string): string =>
  getContrastRatio(background, READABLE_PAPER) >=
  getContrastRatio(background, READABLE_INK)
    ? READABLE_PAPER
    : READABLE_INK;
